'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type {
  GTMEventGlycerinBaseClick,
  GTMEventGlycerinDisplayOverlay,
} from '@packages/tracking/src/types/events';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecipientInformationGql } from '../helpers/useRecipientInformationGql';
import { DoiNotificationContent } from './DoiNotificationContent.client';
import { hasBeenClosed } from './helpers/closed';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';
import { subscribeSheetMessages } from './NewsletterSheet.messages';
import { NewsletterSubscribeCompact } from './NewsletterSubscribeCompact.client';
import { Sheet } from './Sheet.client';

const NotificationChild =
  (email: string) =>
  ({ closeNotification }: { closeNotification: () => void }) => (
    <DoiNotificationContent email={email} onClickedSuccess={closeNotification} />
  );

/**
 * The Newsletter Sheet Container Component:
 * A dialog at the bottom of the screen that is used to subscribe to the newsletter.
 *
 * It also checks your subscription state and hides itself if the user is already subscribed.
 * If the confirmation is still pending, a notification is displayed to remind the user.
 *
 * State:
 * - `forceHidden`: Boolean state to force hide the sheet.
 * - `notification`: Object containing notification state with `show` and `message`.
 *
 * Returns:
 * - `null` if the sheet should be hidden.
 * - A `Box` component containing the notification and the appropriate newsletter sheet component.
 */
export const NewsletterSheet = () => {
  const { nlTexts } = useNlSheetTexts();
  const intl = useIntl();
  const {
    forms: {
      newsletterSettings: { useDOI },
      newsletterSheet: { enabled: sheetEnabled },
    },
  } = useConfig();
  const { jwtToken } = useSession();
  const { loggedIn } = useLoginState();
  const dispatchGTMEvent = useTracking();
  const { addNotification } = useNotification();
  const closed = hasBeenClosed();

  const [forceHidden, setForceHidden] = useState(false);
  const { setCookie, getCookies } = useCookies();
  const { hasPermission, pending, email } = useRecipientInformationGql();

  const truncatedSheetToken = jwtToken?.slice(0, 30);
  const isNotificationOpen = getCookies()['news-sheet-disabled'] !== truncatedSheetToken;

  useEffect(() => {
    if (forceHidden) {
      localStorage.setItem('newsletterSheetClosed', `${Date.now()}`);
      dispatchGTMEvent<GTMEventGlycerinBaseClick>({
        event: 'BaseClick',
        BaseClickData: {
          category: 'newsletter-sheet',
          label: 'canceled',
        },
      });
    }
  }, [forceHidden, dispatchGTMEvent]);

  /**
   * Hide the sheet during login process, if it has been closed,
   * or it should be hidden for a logged in customer
   * */
  const showSheet =
    !forceHidden && sheetEnabled && !closed && (!loggedIn || (!hasPermission && !pending));

  useEffect(() => {
    if (showSheet) {
      dispatchGTMEvent<GTMEventGlycerinDisplayOverlay>({
        event: 'DisplayOverlay',
        DisplayOverlayData: {
          category: 'newsletter-sheet',
          label: 'show',
        },
      });
    }
  }, [dispatchGTMEvent, showSheet]);

  const isShowNotification = isNotificationOpen && pending && email && useDOI;

  /**
   * Notification for pending DOI E-Mail.
   * This component is used instead of `addToast` to have a component as the message child.
   *
   * Note (INSPIRE-2154):
   * - The pending layer should only be displayed in the first 6 months after registration.
   * - To be precise, after the first declaration of intent in the DOI process.
   * - During this period, the layer should be displayed once per session for 15 seconds.
   */
  useEffect(() => {
    if (isShowNotification && email) {
      addNotification({
        duration: 15000,
        severity: 'warning',
        ContentOverride: NotificationChild(email),
        onClose: () => {
          if (jwtToken) {
            setCookie(null, 'news-sheet-disabled', `${truncatedSheetToken}`, {
              path: '/',
            });
          }
        },
      });
    }
  }, [addNotification, email, isShowNotification, jwtToken, setCookie, truncatedSheetToken]);

  if (!showSheet) {
    return null;
  }

  return (
    <Sheet
      title={nlTexts?.label ?? intl.formatMessage(subscribeSheetMessages.sheetTitle)}
      onClickClose={() => setForceHidden(true)}
    >
      <NewsletterSubscribeCompact closeNlSheet={() => setForceHidden(true)} />
    </Sheet>
  );
};
